import { useEffect, useRef, useState } from "react";

export default function CustomSelect({
    value,
    onChange,
    options = [],
    placeholder = "Bitte auswählen",
    disabled = false,
    allowEmpty = true,
    className = "",
    buttonClassName = "",
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const normalizedValue = value === undefined || value === null ? "" : String(value);
    const normalizedOptions = options.map((option) => ({
        ...option,
        value: option.value === undefined || option.value === null ? "" : String(option.value),
        label: option.label === undefined || option.label === null ? "" : String(option.label),
    }));
    const visibleOptions = allowEmpty
        ? [{ value: "", label: placeholder }, ...normalizedOptions]
        : normalizedOptions;
    const selected = normalizedOptions.find((option) => option.value === normalizedValue);
    const displayLabel = selected?.label || placeholder;

    useEffect(() => {
        if (!open) return undefined;

        function handlePointerDown(event) {
            if (!ref.current?.contains(event.target)) {
                setOpen(false);
            }
        }

        function handleKeyDown(event) {
            if (event.key === "Escape") {
                setOpen(false);
            }
        }

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (disabled) {
            setOpen(false);
        }
    }, [disabled]);

    function selectValue(nextValue) {
        onChange?.(nextValue);
        setOpen(false);
    }

    return (
        <div className={`rv-custom-select ${open ? "rv-custom-select--open" : ""} ${className}`.trim()} ref={ref}>
            <button
                type="button"
                className={`input rv-select rv-custom-select__button ${buttonClassName}`.trim()}
                onClick={() => {
                    if (!disabled) setOpen((current) => !current);
                }}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={selected ? "" : "rv-custom-select__placeholder"}>
                    {displayLabel}
                </span>
            </button>

            {open && (
                <div className="rv-custom-select__menu" role="listbox">
                    {visibleOptions.map((option) => {
                        const isSelected = option.value === normalizedValue;

                        return (
                            <button
                                key={`${option.value}-${option.label}`}
                                type="button"
                                className={`rv-custom-select__option ${isSelected ? "rv-custom-select__option--selected" : ""}`}
                                onClick={() => selectValue(option.value)}
                                disabled={option.disabled}
                                role="option"
                                aria-selected={isSelected}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
